<template>
  <div>
    <a-form class="ant-advanced-search-form" :form="depotSearchForm" @submit="handleDepotSearch">
      <a-row :gutter="24">
        <a-col :span="8" v-if="!this.SingleCompanyUser">
          <a-form-item label="Company">
            <a-select
              @change="onCompanyChange"
              v-decorator="['company']"
              :allowClear="true"
              placeholder="Select company"
            >
              <a-select-option v-for="(item, index) in displayCompanies" :key="index" :value="item">
                {{ item }}
              </a-select-option>
            </a-select>
          </a-form-item>
        </a-col>
        <a-col :span="8">
          <a-form-item label="Location">
            <a-select
              v-decorator="[
                'depot',
                {
                  initialValue: depotList.length == 1 ? depotList[0] : undefined
                }
              ]"
              :allowClear="true"
              placeholder="Select location"
              :disabled="depotDisabled"
            >
              <a-select-option v-for="(item, index) in depotList" :key="index" :value="item">
                {{ item }}
              </a-select-option>
            </a-select>
          </a-form-item>
        </a-col>
        <a-col :span="8" :style="{ textAlign: 'right' }" :offset="getOffsetValue">
          <a-form-item>
            <a-button type="primary" html-type="submit" icon="search">
              Search Depots
            </a-button>
            <a-tooltip
              placement="top"
              title="Select depots from the same company to perform group actions."
              v-if="isAdminUser"
            >
              <a-dropdown :trigger="['click']">
                <a-menu slot="overlay" @click="handleGroupActionClick">
                  <a-menu-item key="moveChargers" :disabled="moveChargersButtonDisabled">
                    <a-tooltip placement="top" title="Select two depots from the same company to perform this action."
                      ><a-icon type="swap" />Move Chargers
                    </a-tooltip></a-menu-item
                  >
                  <a-menu-divider />
                  <a-menu-item key="allowUnrestrictedCharging" :disabled="!hasSelectedMultiple">
                    <a-icon type="clock-circle" />Allow Unrestricted Charging
                  </a-menu-item>
                  <a-menu-item key="removeUnrestrictedCharging" :disabled="!hasSelectedMultiple">
                    <a-icon type="close" />Remove Unrestricted Charging
                  </a-menu-item>
                  <a-menu-item key="addTimeSlot" :disabled="!hasSelectedMultiple">
                    <a-icon type="plus" />Add Time Slot
                  </a-menu-item>
                </a-menu>
                <a-button style="margin-left: 8px" type="primary"> Group Actions <a-icon type="down" /> </a-button>
              </a-dropdown>
            </a-tooltip>
          </a-form-item>
        </a-col>
      </a-row>
    </a-form>
    <br />
    <div class="search-result-list">
      <a-table
        :columns="depotColumns"
        :dataSource="handleDepotData"
        :scroll="{ x: 1500, y: 1000 }"
        :loading="depotDataLoading"
        :rowKey="record => record.key"
        :pagination="{
          defaultPageSize: 10,
          showSizeChanger: true,
          position: 'both'
        }"
        bordered
        :row-selection="rowSelection"
      >
        <span slot="serverSyncStatus" slot-scope="depotRecord">
          <span v-if="depotRecord.syncStatus === 'true'">
            <a-badge status="success">
              <a-tooltip title="All settings synced!">Synced</a-tooltip>
            </a-badge>
          </span>
          <span v-else-if="depotRecord.syncStatus === 'false'">
            <a-badge status="error">
              <a-tooltip title="Click to sync!" v-if="isAdminUser">
                <a @click="showResyncConfirm(depotRecord)">Not Synced</a>
              </a-tooltip>
              <a-tooltip title="Notify admin to sync the server!" v-else>
                Not Synced
              </a-tooltip>
            </a-badge>
          </span>
          <span v-else-if="depotRecord.syncStatus === 'error'">
            <a-badge status="warning">
              <a-tooltip title="Click to retry!">
                <a @click="getSingleSyncStatus(depotRecord)">Error Retrieving Status</a>
              </a-tooltip>
            </a-badge>
          </span>
          <span v-else>
            <a-badge status="default">
              <a-tooltip title="IoT connection closed abruptly!">Cannot Retrieve Status</a-tooltip>
            </a-badge>
          </span>
        </span>
        <span slot="actions" slot-scope="depotRecord">
          <a
            @click="
              () => {
                addChargerFormInitialValues = depotRecord;
                addChargerModalVisible = true;
              }
            "
            >Add Chargers</a
          >
          <a-divider type="vertical" />
          <a
            @click="
              () => {
                editConfigurationFormInitialValues = depotRecord;
                fleetList = masterCompanyMap.get(depotRecord.company).fleets;
                editConfigurationModalVisible = true;
              }
            "
            >View Configuration</a
          >
          <a-divider type="vertical" />
          <a-dropdown :trigger="['click']">
            <a class="ant-dropdown-link" @click="e => e.preventDefault()"> More <a-icon type="down" /> </a>
            <a-menu slot="overlay">
              <a-menu-item key="1">
                <a
                  @click="
                    () => {
                      chargingScheduleIntitialValues = depotRecord;
                      let timeSlots = JSON.parse(depotRecord.timeSlots);
                      timeSlots.forEach((timeSlot, index) => {
                        timeSlot.rowKey = index;
                      });
                      chargingScheduleDataSource = timeSlots;
                      allowUnrestrictedCharging = checkZeroTimeSlot(chargingScheduleDataSource);
                      chargingScheduleModalVisible = true;
                    }
                  "
                  >View Charging Restriction Schedule</a
                >
              </a-menu-item>
              <a-menu-item key="2">
                <a @click="showResyncConfirm(depotRecord, true)">Resync Server</a>
              </a-menu-item>
            </a-menu>
          </a-dropdown>
        </span>
        <span slot="customerActions" slot-scope="depotRecord">
          <a
            @click="
              () => {
                editConfigurationFormInitialValues = depotRecord;
                fleetList = masterCompanyMap.get(depotRecord.company).fleets;
                editConfigurationModalVisible = true;
              }
            "
            >View Configuration</a
          >
          <a-divider type="vertical" />
          <a
            @click="
              () => {
                chargingScheduleIntitialValues = depotRecord;
                let timeSlots = JSON.parse(depotRecord.timeSlots);
                timeSlots.forEach((timeSlot, index) => {
                  timeSlot.rowKey = index;
                });
                chargingScheduleDataSource = timeSlots;
                allowUnrestrictedCharging = checkZeroTimeSlot(chargingScheduleDataSource);
                chargingScheduleModalVisible = true;
              }
            "
            >View Charging Restriction Schedule</a
          >
        </span>
        <template slot="expandedRowRender" slot-scope="depotRecord">
          <a-table
            :columns="innerDepotColumns"
            :dataSource="
              [...depotRecord.chargerList].sort(
                (recordOne, recordTwo) => recordOne.chargerNumber - recordTwo.chargerNumber
              )
            "
            :pagination="false"
            :rowKey="chargerRecord => chargerRecord.entityId"
          >
            <span slot="actions" slot-scope="chargerRecord">
              <a-popconfirm ok-text="Yes" cancel-text="No" @confirm="deleteCharger(depotRecord, chargerRecord)">
                <template slot="title">
                  <p style="margin:0">
                    Are you sure you want to
                    <span style="color:#f50">delete</span> this charger?
                  </p>
                </template>
                <a>Delete</a>
              </a-popconfirm>
              <a-divider type="vertical" />
              <a
                @click="
                  () => {
                    editChargerFormInitialValues = depotRecord;
                    editChargerFormInitialValues.selectedChargerKey = chargerRecord.key;
                    editChargerFormInitialValues.selectedChargerNumber = chargerRecord.chargerNumber;
                    editChargerFormInitialValues.selectedMacID = chargerRecord.macID;
                    editChargerFormInitialValues.selectedPriority = chargerRecord.priority;
                    editChargerModalVisible = true;
                  }
                "
                >Edit</a
              >
            </span>
          </a-table>
        </template>
        <span slot="footer">
          <span style="float: left; display: block;">
            {{ this.depotEntriesFound + " Depot(s) Found" }}
          </span>
          <span style="text-align: right; display: block;">
            {{ this.totalNumberOfChargers + " Total Chargers" }}
          </span>
        </span>
      </a-table>
      <a-modal
        v-model="addChargerModalVisible"
        title="Add Chargers"
        centered
        :afterClose="
          () => {
            this.addChargerForm.resetFields();
            this.uploadFileList = [];
            this.uploadCSVButtonDisabled = false;
          }
        "
      >
        <a-form layout="vertical" id="addChargerForm" :form="addChargerForm" @submit="addChargers">
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item label="Depot Name">
                <a-input
                  v-decorator="[
                    'depotName',
                    {
                      initialValue: addChargerFormInitialValues.depot
                    }
                  ]"
                  :disabled="true"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item>
                <span slot="label"
                  >Chargers to Add
                  <a-popover title="Charger List Format">
                    <template slot="content">
                      <p>Please provide the list of chargers as a JSON Array. For example:</p>
                      <p>[ { "chargerNumber" : 1, "macID" : "000123456789", "priority" : 1 } ]</p>
                      <p><b>Required Attributes:</b> chargerNumber, macID, priority</p>
                      <p>
                        <b>TIP:</b> Convert CSV to JSON
                        <a href="https://csvjson.com/csv2json" target="_blank" rel="noopener noreferrer">
                          here.
                        </a>
                      </p>
                    </template>
                    <a-icon type="question-circle-o" />
                  </a-popover>
                </span>
                <a-textarea
                  allowClear
                  placeholder="JSON Charger List"
                  :auto-size="{ minRows: 5, maxRows: 10 }"
                  v-decorator="[
                    'chargerList',
                    {
                      rules: [
                        {
                          validator: customAddFormValidator
                        }
                      ]
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item style="text-align: center">
                <a-upload
                  accept=".csv"
                  :headers="headers"
                  :customRequest="dummyUploadRequest"
                  @change="handleUploadChange"
                  :fileList="uploadFileList"
                  :multiple="false"
                >
                  <a-button icon="upload" :disabled="uploadCSVButtonDisabled">Upload CSV</a-button>
                </a-upload>
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item style="text-align: center">
                <a-button
                  icon="plus"
                  @click="
                    () => {
                      addIndividualChargerModalVisible = true;
                    }
                  "
                  >Add Single Charger</a-button
                >
              </a-form-item>
            </a-col>
          </a-row>
        </a-form>
        <template slot="footer">
          <a-button form="addChargerForm" type="primary" html-type="submit" :loading="addChargerFormButtonLoading">
            Add Chargers
          </a-button>
        </template>
      </a-modal>
      <a-modal
        v-model="editChargerModalVisible"
        title="Edit Charger Record"
        centered
        :afterClose="
          () => {
            editChargerForm.resetFields();
            editChargerFormInitialValues = {};
          }
        "
      >
        <a-form layout="vertical" id="editChargerForm" :form="editChargerForm" @submit="editCharger">
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item label="Depot Name">
                <a-input
                  v-decorator="[
                    'depotName',
                    {
                      initialValue: editChargerFormInitialValues.depot
                    }
                  ]"
                  :disabled="true"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="Charger Number">
                <a-input-number
                  v-decorator="[
                    'chargerNumber',
                    {
                      rules: [
                        {
                          validator: customEditFormValidator
                        }
                      ],
                      initialValue: editChargerFormInitialValues.selectedChargerNumber
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Priority">
                <a-input-number
                  v-decorator="[
                    'priority',
                    {
                      rules: [
                        {
                          validator: customEditFormValidator
                        }
                      ],
                      initialValue: editChargerFormInitialValues.selectedPriority
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item label="MAC ID">
                <a-input
                  v-decorator="[
                    'macID',
                    {
                      rules: [
                        {
                          validator: customEditFormValidator
                        }
                      ],
                      initialValue: editChargerFormInitialValues.selectedMacID
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
          </a-row>
        </a-form>
        <template slot="footer">
          <a-button
            form="editChargerForm"
            type="primary"
            key="saveChanges"
            html-type="submit"
            :loading="editChargerFormButtonLoading"
          >
            Save Changes
          </a-button>
        </template>
      </a-modal>
      <a-modal
        v-model="editConfigurationModalVisible"
        title="Configuration Settings"
        centered
        :afterClose="
          () => {
            this.editConfigurationForm.resetFields();
            this.editingDisabled = true;
            this.editConfigurationFormInitialValues = {};
            this.fleetList = [];
          }
        "
      >
        <a-form id="editConfigurationForm" :form="editConfigurationForm" @submit="saveConfiguration">
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item label="Depot Name">
                <a-input
                  v-decorator="[
                    'depot',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a depot name!'
                        }
                      ],
                      initialValue: editConfigurationFormInitialValues.depot
                    }
                  ]"
                  :disabled="true"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item label="Fleet(s)">
                <a-select
                  v-decorator="[
                    'fleetList',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please select at least one fleet!'
                        },
                        {
                          validator: customFleetListValidator
                        }
                      ],
                      initialValue: editConfigurationFormInitialValues.fleetList
                        ? JSON.parse(editConfigurationFormInitialValues.fleetList)
                        : ''
                    }
                  ]"
                  :allowClear="true"
                  :disabled="editingDisabled"
                  mode="multiple"
                  :getPopupContainer="trigger => trigger.parentNode"
                >
                  <a-select-option v-for="(item, index) in fleetList" :key="index" :value="item">
                    {{ item }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="Maximum SOC To Charge (%)">
                <a-input-number
                  v-decorator="[
                    'maxSocToCharge',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a number!'
                        },
                        {
                          type: 'number',
                          min: 1,
                          max: 100,
                          message: 'Value must be a valid number between [1-100]'
                        }
                      ],
                      initialValue: editConfigurationFormInitialValues.maxSocToCharge
                    }
                  ]"
                  :disabled="editingDisabled"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Maximum Concurrent Chargers">
                <a-input-number
                  v-decorator="[
                    'maxConcurrentChargers',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a number!'
                        },
                        {
                          type: 'number',
                          min: 1,
                          message: 'Value must be a non-zero positive number!'
                        }
                      ],
                      initialValue: editConfigurationFormInitialValues.maxConcurrentChargers
                    }
                  ]"
                  :disabled="editingDisabled"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="Maximum Charging Current (amp)">
                <a-input-number
                  v-decorator="[
                    'maxChargingCurrent',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a number!'
                        },
                        {
                          type: 'number',
                          min: 1,
                          message: 'Value must be a non-zero positive number!'
                        }
                      ],
                      initialValue: editConfigurationFormInitialValues.maxChargingCurrent
                    }
                  ]"
                  :disabled="editingDisabled"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Timeout For Disconnect Alert (min)">
                <a-input-number
                  v-decorator="[
                    'timeoutForDisconnectAlert',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a number!'
                        },
                        {
                          type: 'number',
                          min: 1,
                          message: 'Value must be a non-zero positive number!'
                        }
                      ],
                      initialValue: editConfigurationFormInitialValues.timeoutForDisconnectAlert
                    }
                  ]"
                  :disabled="editingDisabled"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="Count Overridden Charger">
                <a-radio-group
                  v-decorator="[
                    'overriddenChargerCount',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please select at least one choice!'
                        }
                      ],
                      initialValue:
                        editConfigurationFormInitialValues.overriddenChargerCount != null
                          ? editConfigurationFormInitialValues.overriddenChargerCount.toString()
                          : null
                    }
                  ]"
                  :disabled="editingDisabled"
                >
                  <a-radio value="true">
                    Yes
                  </a-radio>
                  <a-radio value="false">
                    No
                  </a-radio>
                </a-radio-group>
              </a-form-item>
            </a-col>
          </a-row>
        </a-form>
        <template slot="footer">
          <a-button @click="() => (this.editingDisabled = false)" v-if="isAdminUser">
            Edit
          </a-button>
          <a-tooltip placement="top" title="Only users with admin access can make changes!" v-else>
            <a-button disabled style="margin-right: 8px">
              Edit
            </a-button>
          </a-tooltip>
          <a-button
            form="editConfigurationForm"
            type="primary"
            html-type="submit"
            :loading="editConfigurationFormButtonLoading"
            :disabled="editingDisabled"
          >
            Save Changes
          </a-button>
        </template>
      </a-modal>
      <a-modal
        v-model="chargingScheduleModalVisible"
        title="Charging Restriction Schedule"
        centered
        :afterClose="
          () => {
            this.chargingScheduleForm.resetFields();
            this.chargingScheduleIntitialValues = {};
            this.chargingScheduleDataSource = [];
            this.allowUnrestrictedCharging = false;
          }
        "
        width="700"
        id="backModal"
      >
        <a-form id="chargingScheduleForm" :form="chargingScheduleForm" @submit="saveChargingSchedule">
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item>
                <a-table
                  :columns="chargingScheduleColumns"
                  :dataSource="chargingScheduleDataSource"
                  :pagination="false"
                  :rowKey="(record, index) => index + 1"
                  bordered
                >
                  <span slot="actions" slot-scope="timeSlot">
                    <a-popconfirm title="Sure to delete?" @confirm="() => deleteTimeSlot(timeSlot.rowKey)">
                      <a>Delete</a>
                    </a-popconfirm>
                    <a-divider type="vertical" />
                    <a
                      @click="
                        () => {
                          editTimeSlotFormInitialValues = timeSlot;
                          editTimeSlotModalVisible = true;
                        }
                      "
                      >Edit</a
                    >
                  </span>
                </a-table>
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24" style="text-align: center">
            <a-col :span="12" v-if="isAdminUser">
              <a-form-item>
                <a-button
                  icon="plus"
                  @click="
                    () => {
                      addTimeSlotModalVisible = true;
                    }
                  "
                  >Add Time Slot</a-button
                >
              </a-form-item>
            </a-col>
            <a-col :span="12 + (isAdminUser ? 0 : 12)">
              <a-form-item>
                <a-checkbox
                  v-decorator="['allowUnrestrictedCharging']"
                  :checked="allowUnrestrictedCharging"
                  @change="handleCheckboxChange"
                  :disabled="!isAdminUser"
                >
                  Allow Unrestricted Charging
                  <a-tooltip title="Selecting this will disregard all present restrictions and allow charging 24/7.">
                    <a-icon type="question-circle-o" />
                  </a-tooltip>
                </a-checkbox>
              </a-form-item>
            </a-col>
          </a-row>
        </a-form>
        <template slot="footer">
          <a-button
            form="chargingScheduleForm"
            type="primary"
            html-type="submit"
            :loading="chargingScheduleButtonLoading"
            v-if="isAdminUser"
          >
            Save Changes
          </a-button>
          <a-tooltip placement="top" title="Only users with admin access can make changes!" v-else>
            <a-button disabled>
              Save Changes
            </a-button>
          </a-tooltip>
        </template>
      </a-modal>
      <a-modal
        v-model="addIndividualChargerModalVisible"
        title="Add Single Charger"
        centered
        :afterClose="
          () => {
            this.addIndividualChargerForm.resetFields();
          }
        "
      >
        <a-form id="addIndividualChargerForm" :form="addIndividualChargerForm" @submit="addIndividualCharger">
          <a-row :gutter="24">
            <a-col :span="8">
              <a-form-item label="Charger Number">
                <a-input-number
                  v-decorator="[
                    'chargerNumber',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a charger number!'
                        }
                      ]
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
            <a-col :span="8">
              <a-form-item label="MAC ID">
                <a-input
                  v-decorator="[
                    'macID',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a MAC ID!'
                        }
                      ]
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
            <a-col :span="8">
              <a-form-item label="Priority">
                <a-input-number
                  v-decorator="[
                    'priority',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a priority!'
                        }
                      ]
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
          </a-row>
        </a-form>
        <template slot="footer">
          <a-button form="addIndividualChargerForm" type="primary" html-type="submit">
            Add Charger
          </a-button>
        </template>
      </a-modal>
      <a-modal
        v-model="addTimeSlotModalVisible"
        title="Add No Charging Time Slot"
        centered
        :afterClose="
          () => {
            this.addTimeSlotForm.resetFields();
            this.addTimeSlotFormButtonLoading = false;
          }
        "
        id="addTimeSlot"
      >
        <a-form id="addTimeSlotForm" :form="addTimeSlotForm" @submit="addTimeSlot">
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="No Charging Start Time">
                <a-time-picker
                  v-decorator="[
                    'noChargingStartTime',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a time!'
                        }
                      ],
                      initialValue: this.$moment('12:15', 'HH:mm')
                    }
                  ]"
                  format="HH:mm"
                  :getPopupContainer="trigger => trigger.parentNode"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="No Charging Stop Time">
                <a-time-picker
                  v-decorator="[
                    'noChargingStopTime',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a time!'
                        }
                      ],
                      initialValue: this.$moment('15:15', 'HH:mm')
                    }
                  ]"
                  format="HH:mm"
                  :getPopupContainer="trigger => trigger.parentNode"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="Is Weekend Excluded">
                <a-radio-group
                  v-decorator="[
                    'isWeekendExcluded',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please select at least one choice!'
                        }
                      ],
                      initialValue: 'false'
                    }
                  ]"
                >
                  <a-radio value="true">
                    Yes
                  </a-radio>
                  <a-radio value="false">
                    No
                  </a-radio>
                </a-radio-group>
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Is Overnight">
                <a-radio-group
                  v-decorator="[
                    'isOvernight',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please select at least one choice!'
                        }
                      ],
                      initialValue: 'false'
                    }
                  ]"
                >
                  <a-radio value="true">
                    Yes
                  </a-radio>
                  <a-radio value="false">
                    No
                  </a-radio>
                </a-radio-group>
              </a-form-item>
            </a-col>
          </a-row>
        </a-form>
        <template slot="footer">
          <a-button form="addTimeSlotForm" type="primary" html-type="submit" :loading="addTimeSlotFormButtonLoading">
            Add Time Slot
          </a-button>
        </template>
      </a-modal>
      <a-modal
        v-model="editTimeSlotModalVisible"
        title="Edit No Charging Time Slot"
        centered
        :afterClose="
          () => {
            this.editTimeSlotForm.resetFields();
          }
        "
        id="editTimeSlot"
      >
        <a-form id="editTimeSlotForm" :form="editTimeSlotForm" @submit="editTimeSlot">
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="No Charging Start Time">
                <a-time-picker
                  v-decorator="[
                    'noChargingStartTime',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a time!'
                        }
                      ],
                      initialValue: this.$moment(editTimeSlotFormInitialValues.noChargingStartTime, 'HH:mm')
                    }
                  ]"
                  format="HH:mm"
                  :getPopupContainer="trigger => trigger.parentNode"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="No Charging Stop Time">
                <a-time-picker
                  v-decorator="[
                    'noChargingStopTime',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a time!'
                        }
                      ],
                      initialValue: this.$moment(editTimeSlotFormInitialValues.noChargingStopTime, 'HH:mm')
                    }
                  ]"
                  format="HH:mm"
                  :getPopupContainer="trigger => trigger.parentNode"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="Is Weekend Excluded">
                <a-radio-group
                  v-decorator="[
                    'isWeekendExcluded',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please select at least one choice!'
                        }
                      ],
                      initialValue:
                        editTimeSlotFormInitialValues.isWeekendExcluded != null
                          ? editTimeSlotFormInitialValues.isWeekendExcluded.toString()
                          : null
                    }
                  ]"
                >
                  <a-radio value="true">
                    Yes
                  </a-radio>
                  <a-radio value="false">
                    No
                  </a-radio>
                </a-radio-group>
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Is Overnight">
                <a-radio-group
                  v-decorator="[
                    'isOvernight',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please select at least one choice!'
                        }
                      ],
                      initialValue:
                        editTimeSlotFormInitialValues.isOvernight != null
                          ? editTimeSlotFormInitialValues.isOvernight.toString()
                          : null
                    }
                  ]"
                >
                  <a-radio value="true">
                    Yes
                  </a-radio>
                  <a-radio value="false">
                    No
                  </a-radio>
                </a-radio-group>
              </a-form-item>
            </a-col>
          </a-row>
        </a-form>
        <template slot="footer">
          <a-button form="editTimeSlotForm" type="primary" html-type="submit">
            Save Time Slot
          </a-button>
        </template>
      </a-modal>
      <a-modal
        v-model="moveChargersModalVisible"
        title="Move Chargers"
        centered
        width="700"
        :afterClose="
          () => {
            this.moveChargersTitles = [];
            this.moveChargersData = [];
            this.moveChargersTargetKeys = [];
            this.moveChargersCacheData = [];
            this.moveChargersForm.resetFields();
            this.editingKey = '';
            this.moveChargersLoadingPercentage = 0;
          }
        "
      >
        <a-form id="moveChargersForm" :form="moveChargersForm" @submit="saveMoveChargers">
          <a-form-item>
            <a-transfer
              :dataSource="moveChargersData"
              :targetKeys="moveChargersTargetKeys"
              :titles="moveChargersTitles"
              @change="handleMoveChargersChange"
              v-decorator="['transferTable']"
              :showSelectAll="false"
            >
              <template
                slot="children"
                slot-scope="{ props: { filteredItems, selectedKeys }, on: { itemSelectAll, itemSelect } }"
              >
                <a-table
                  :row-selection="getRowSelection({ selectedKeys, itemSelectAll, itemSelect })"
                  :columns="moveChargerColumns"
                  :dataSource="filteredItems"
                  bordered
                >
                  <template
                    v-for="columnName in ['chargerNumber', 'priority']"
                    :slot="columnName"
                    slot-scope="text, record"
                  >
                    <div :key="columnName">
                      <a-input-number
                        v-if="record.editable"
                        style="margin: -5px 0"
                        :value="text"
                        @change="value => handleChargerRecordChange(value, record.key, columnName)"
                      />
                      <template v-else>
                        {{ text }}
                      </template>
                    </div>
                  </template>
                  <template slot="actions" slot-scope="text, record">
                    <span v-if="record.editable">
                      <a @click="() => saveChargerRecord(record.key)">Save</a>
                      <a-divider type="vertical" />
                      <a-popconfirm title="Sure to cancel?" @confirm="() => cancelChargerRecordChange(record.key)">
                        <a>Cancel</a>
                      </a-popconfirm>
                    </span>
                    <span v-else>
                      <a :disabled="editingKey !== ''" @click="() => editChargerRecord(record.key)">Edit</a>
                    </span>
                  </template>
                </a-table>
              </template>
            </a-transfer>
          </a-form-item>
        </a-form>
        <template slot="footer">
          <a-row>
            <a-col :span="16">
              <a-progress :percent="moveChargersLoadingPercentage" status="active" />
            </a-col>
            <a-col :span="8">
              <a-button
                form="moveChargersForm"
                :loading="moveChargersFormButtonLoading"
                type="primary"
                html-type="submit"
                :disabled="editingKey !== ''"
              >
                Save Changes
              </a-button>
            </a-col>
          </a-row>
        </template>
      </a-modal>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { difference, cloneDeep } from "lodash";

export default {
  data() {
    return {
      depotSearchForm: this.$form.createForm(this),
      editChargerForm: this.$form.createForm(this),
      addChargerForm: this.$form.createForm(this),
      addTimeSlotForm: this.$form.createForm(this),
      editTimeSlotForm: this.$form.createForm(this),
      editConfigurationForm: this.$form.createForm(this),
      moveChargersForm: this.$form.createForm(this),
      addIndividualChargerForm: this.$form.createForm(this),
      chargingScheduleForm: this.$form.createForm(this),
      depotData: [],
      chargingScheduleDataSource: [],
      uploadFileList: [],
      fleetList: [],
      depotList: [],
      selectedRowKeys: [],
      selectedRows: [],
      moveChargersData: [],
      moveChargersCacheData: [],
      moveChargersTargetKeys: [],
      moveChargersTitles: [],
      editChargerFormInitialValues: {},
      addChargerFormInitialValues: {},
      editTimeSlotFormInitialValues: {},
      editConfigurationFormInitialValues: {},
      chargingScheduleIntitialValues: {},
      depotDataLoading: false,
      editChargerModalVisible: false,
      editChargerFormButtonLoading: false,
      addChargerModalVisible: false,
      addIndividualChargerModalVisible: false,
      addTimeSlotModalVisible: false,
      addTimeSlotFormButtonLoading: false,
      editTimeSlotModalVisible: false,
      addChargerFormButtonLoading: false,
      editConfigurationModalVisible: false,
      editConfigurationFormButtonLoading: false,
      moveChargersModalVisible: false,
      moveChargersFormButtonLoading: false,
      chargingScheduleModalVisible: false,
      chargingScheduleButtonLoading: false,
      editingDisabled: true,
      depotDisabled: true,
      allowUnrestrictedCharging: false,
      uploadCSVButtonDisabled: false,
      uploadTXTButtonDisabled: false,
      isAdminUser: false,
      depotEntriesFound: 0,
      totalNumberOfChargers: 0,
      moveChargersLoadingPercentage: 0,
      editingKey: "",
      headers: {
        authorization: "authorization-text"
      },
      moveChargerColumns: [
        {
          dataIndex: "chargerNumber",
          title: "Charger Number",
          align: "center",
          scopedSlots: { customRender: "chargerNumber" }
        },
        {
          dataIndex: "macID",
          title: "MAC Address",
          align: "center"
        },
        {
          dataIndex: "priority",
          title: "Priority",
          align: "center",
          scopedSlots: { customRender: "priority" }
        },
        {
          title: "Actions",
          align: "center",
          scopedSlots: { customRender: "actions" }
        }
      ]
    };
  },

  props: ["AWS", "cognitoidentityserviceprovider", "masterCompanyMap"],

  computed: {
    ...mapState(["UserName", "PoolData", "CompanyMap", "SingleCompanyUser", "AccessGroups"]),

    // function to display the list of companies user is authenticated for.
    displayCompanies() {
      let companyList = [];
      this.CompanyMap.forEach(function(value, key) {
        companyList.push(key);
      });
      return companyList.sort();
    },

    getOffsetValue() {
      if (this.SingleCompanyUser) {
        return 8;
      }
      return 0;
    },

    handleDepotData() {
      // copy data over, assign key to each record, and sort/filter it according to requirements.
      const data = this.depotData;
      var totalChargerCount = 0;
      data
        .sort((recordOne, recordTwo) => {
          return recordOne.entityId.localeCompare(recordTwo.entityId);
        })
        .forEach((depotObject, outerIndex) => {
          depotObject.key = outerIndex + 1;
          depotObject.chargerList = JSON.parse(depotObject.chargerList);
          depotObject.numberOfChargers = depotObject.chargerList.length;
          totalChargerCount += depotObject.chargerList.length;
          depotObject.chargerList.forEach((charger, innerIndex) => {
            charger.key = innerIndex + 1;
            charger.revision = this.$moment(charger.revision)
              .local()
              .format("YYYY-MM-DD HH:mm");
          });
        });
      this.setTotalNumberOfChargers(totalChargerCount);
      return data;
    },

    moveChargersButtonDisabled() {
      return !(this.selectedRowKeys.length == 2 && this.selectedRows[0].company == this.selectedRows[1].company);
    },

    hasSelectedMultiple() {
      let otherDepots = this.selectedRows.filter(depot => {
        return depot.company !== this.selectedRows[0].company;
      });
      return this.selectedRowKeys.length >= 2 && !otherDepots.length;
    },

    rowSelection() {
      return this.isAdminUser
        ? {
            selectedRowKeys: this.selectedRowKeys,
            onChange: (selectedRowKeys, selectedRows) => {
              this.onSelectChange(selectedRowKeys, selectedRows);
            }
          }
        : null;
    },

    depotColumns() {
      return [
        {
          dataIndex: "depot",
          title: "Depot Name",
          align: "center",
          width: 220
        },
        {
          dataIndex: "company",
          title: "Company",
          align: "center"
        },
        {
          dataIndex: "numberOfChargers",
          title: "Number of Chargers",
          align: "center"
        },
        {
          title: () => {
            return (
              <span>
                Server Sync Status&nbsp;
                <a-tooltip title="Displays whether the server program's configuration settings are synced with the database.">
                  <a-icon type="info-circle" />
                </a-tooltip>
              </span>
            );
          },
          align: "center",
          scopedSlots: { customRender: "serverSyncStatus" },
          width: 200
        },
        {
          title: "Last Updated",
          dataIndex: "revision",
          customRender: revision => {
            if (revision) {
              return this.$moment(revision)
                .local()
                .format("YYYY-MM-DD HH:mm");
            } else {
              return "N/A";
            }
          },
          align: "center"
        },
        {
          title: "Updated By",
          dataIndex: "updatedBy",
          align: "center"
        },
        {
          title: "Actions",
          scopedSlots: { customRender: this.isAdminUser ? "actions" : "customerActions" },
          align: "center",
          width: 250
        }
      ];
    },

    innerDepotColumns() {
      let innerDepotColumns = [
        {
          dataIndex: "chargerNumber",
          title: "Charger Number",
          align: "center",
          sorter: (recordOne, recordTwo) => recordOne.chargerNumber - recordTwo.chargerNumber
        },
        {
          dataIndex: "macID",
          title: "MAC Address",
          align: "center",
          sorter: (recordOne, recordTwo) => recordOne.macID.localeCompare(recordTwo.macID)
        },
        {
          dataIndex: "priority",
          title: "Priority",
          align: "center",
          sorter: (recordOne, recordTwo) => recordOne.priority - recordTwo.priority
        },
        {
          title: "Last Updated",
          dataIndex: "revision",
          align: "center"
        }
      ];
      if (this.isAdminUser) {
        innerDepotColumns.push({
          title: "Actions",
          scopedSlots: { customRender: "actions" },
          align: "center"
        });
      }
      return innerDepotColumns;
    },

    chargingScheduleColumns() {
      let chargingScheduleColumns = [
        {
          dataIndex: "noChargingStartTime",
          title: "No Charging Start Time",
          align: "center"
        },
        {
          dataIndex: "noChargingStopTime",
          title: "No Charging Stop Time",
          align: "center"
        },
        {
          dataIndex: "isOvernight",
          title: "Is Overnight",
          align: "center",
          customRender: isOvernight => {
            if (isOvernight) {
              return "Yes";
            } else {
              return "No";
            }
          }
        },
        {
          dataIndex: "isWeekendExcluded",
          title: "Is Weekend Excluded",
          align: "center",
          customRender: isWeekendExcluded => {
            if (isWeekendExcluded) {
              return "Yes";
            } else {
              return "No";
            }
          }
        }
      ];
      if (this.isAdminUser) {
        chargingScheduleColumns.push({
          title: "Actions",
          scopedSlots: { customRender: "actions" },
          align: "center"
        });
      }
      return chargingScheduleColumns;
    }
  },

  methods: {
    handleDepotSearch(e) {
      e.preventDefault();
      this.depotSearchForm.validateFields((error, fieldValues) => {
        if (error) {
          return;
        }
        this.loadDepotData(fieldValues.company, fieldValues.depot);
      });
    },

    editCharger(event) {
      event.preventDefault();
      this.editChargerForm.validateFields(async (error, fieldValues) => {
        if (error) {
          return;
        }
        try {
          this.editChargerFormButtonLoading = true;
          let newDepotRecord = cloneDeep(this.editChargerFormInitialValues);
          let currentChargerList = newDepotRecord.chargerList.filter(charger => {
            return charger.key !== newDepotRecord.selectedChargerKey;
          });
          let newChargerRecord = {
            chargerNumber: fieldValues.chargerNumber,
            macID: fieldValues.macID.trim(),
            priority: fieldValues.priority,
            revision: new Date(),
            entityId: "CHG#" + fieldValues.macID.trim(),
            depot: fieldValues.depotName
          };
          currentChargerList.push(newChargerRecord);
          newDepotRecord.chargerList = currentChargerList;
          // remove all the unnecessary initial values before inserting the updated depotRecord into the database.
          delete newDepotRecord.selectedChargerKey;
          delete newDepotRecord.selectedMacID;
          delete newDepotRecord.selectedChargerNumber;
          delete newDepotRecord.selectedPriority;
          var params = {
            objectType: "DPO",
            operation: "update",
            region: this.masterCompanyMap.get(newDepotRecord.company).region
          };
          var body = {
            depotRecord: this.prepareForDatabase(newDepotRecord),
            insertChargerObjects: true
          };
          await this.sendRequest(params, body, "post");
          let isServerUpdated = await this.updateSingleServerProgram(
            newDepotRecord.depot,
            this.masterCompanyMap.get(newDepotRecord.company).region,
            "chargerList"
          );
          if (!(isServerUpdated === true)) {
            throw new Error("Unable to update server program. Please try again or re-sync the server!");
          }
          this.editChargerFormButtonLoading = false;
          this.editChargerForm.resetFields();
          this.editChargerModalVisible = false;
          this.$message.success("Successfully updated charger and server program!");
          this.loadDepotData(
            this.depotSearchForm.getFieldValue("company"),
            this.depotSearchForm.getFieldValue("depot")
          );
        } catch (error) {
          this.editChargerFormButtonLoading = false;
          this.editChargerForm.resetFields();
          this.editChargerModalVisible = false;
          this.$message.error("Error updating charger - " + (error.message ? error.message : error));
          this.loadDepotData(
            this.depotSearchForm.getFieldValue("company"),
            this.depotSearchForm.getFieldValue("depot")
          );
          return;
        }
      });
    },

    sendRequest(params, body, requestType) {
      // before making an API request, check to make sure user has access to perform the action / credentials need to be refreshed.
      return new Promise((resolve, reject) => {
        this.AWS.config.credentials.get(async credentialError => {
          if (credentialError) {
            if (credentialError.name === "NotAuthorizedException") {
              reject(
                new Error(
                  "You are not authorized to perform this action. Please contact Support Team if you think this is a mistake."
                )
              );
            } else {
              reject(credentialError.message);
            }
          } else {
            try {
              var aws4 = require("aws4");
              let paramsUrl = this.createParamsUrl(params);
              switch (requestType) {
                case "get": {
                  let request = {
                    host: "v9ovun9ana.execute-api.us-west-2.amazonaws.com",
                    method: "GET",
                    url: "https://v9ovun9ana.execute-api.us-west-2.amazonaws.com/prod/configure?" + paramsUrl,
                    path: "/prod/configure?" + paramsUrl,
                    service: "execute-api"
                  };
                  let signedRequest = aws4.sign(request, {
                    secretAccessKey: this.AWS.config.credentials.secretAccessKey,
                    accessKeyId: this.AWS.config.credentials.accessKeyId,
                    sessionToken: this.AWS.config.credentials.sessionToken
                  });
                  // delete unsafe headers from the signed request.
                  delete signedRequest.headers["Host"];
                  delete signedRequest.headers["Content-Length"];

                  let response = await this.$axios(signedRequest);
                  let items = response.data.Items;
                  resolve(items);
                  break;
                }
                case "post": {
                  let request = {
                    host: "v9ovun9ana.execute-api.us-west-2.amazonaws.com",
                    method: "POST",
                    url: "https://v9ovun9ana.execute-api.us-west-2.amazonaws.com/prod/configure?" + paramsUrl,
                    path: "/prod/configure?" + paramsUrl,
                    service: "execute-api",
                    data: body,
                    body: JSON.stringify(body),
                    headers: {
                      "content-type": "application/json"
                    }
                  };
                  let signedRequest = aws4.sign(request, {
                    secretAccessKey: this.AWS.config.credentials.secretAccessKey,
                    accessKeyId: this.AWS.config.credentials.accessKeyId,
                    sessionToken: this.AWS.config.credentials.sessionToken
                  });
                  // delete unsafe headers from the signed request.
                  delete signedRequest.headers["Host"];
                  delete signedRequest.headers["Content-Length"];

                  let response = await this.$axios(signedRequest);
                  let responseData = response.data;
                  resolve(responseData);
                  break;
                }
                default:
                  break;
              }
            } catch (error) {
              // if no response is available, means it is a network error.
              if (!error.response) {
                reject(new Error("Network Error. Please try again!"));
              } else {
                let response = error.response;
                if (response.status == 403) {
                  reject(
                    "You are not authorized to perform this action. Please contact Support Team if you think this is a mistake."
                  );
                } else {
                  reject(response.data.message);
                }
              }
            }
          }
        });
      });
    },

    updateSingleServerProgram(depotName, region, updateAttribute) {
      return new Promise((resolve, reject) => {
        var AWSIoTData = require("aws-iot-device-sdk");
        var isUpdated = false;
        this.AWS.config.credentials.get(identityError => {
          if (!identityError) {
            try {
              const mqttClient = AWSIoTData.device({
                region: region,
                host: "a3ijg5gjke8m6j-ats.iot." + region + ".amazonaws.com",
                clientId: "elmsWebsiteClient_" + this.UserName,
                protocol: "wss",
                debug: false,
                reconnectPeriod: 0,
                accessKeyId: this.AWS.config.credentials.accessKeyId,
                secretKey: this.AWS.config.credentials.secretAccessKey,
                sessionToken: this.AWS.config.credentials.sessionToken
              });
              mqttClient.on("close", () => {
                // end the client connection again in case connection was automatically closed.
                mqttClient.end();
                resolve(isUpdated);
              });
              mqttClient.on("connect", () => {
                // on connection, firstly subscribe to update confirmation topic.
                mqttClient.subscribe("ioc/" + depotName + "/elmsServer/cmd/confirmServerUpdate", { qos: 1 });
                // next, publish message to update the requested attribute and set a timeout period to end the client connection.
                let messagePayload = {
                  updateAttribute: updateAttribute,
                  timestamp: new Date()
                };
                mqttClient.publish(
                  "ioc/" + depotName + "/elmsServer/cmd/issueServerUpdate",
                  JSON.stringify(messagePayload),
                  { qos: 1 }
                );
                setTimeout(() => {
                  // once timeout is reached, end the client connection.
                  mqttClient.end();
                }, 2500);
              });
              mqttClient.on("error", () => {
                throw new Error("Error thrown by MQTT Client.");
              });
              mqttClient.on("message", (topic, payload) => {
                isUpdated = JSON.parse(payload).isUpdated;
                mqttClient.end();
              });
            } catch (error) {
              reject(error);
            }
          } else {
            if (identityError.name === "NotAuthorizedException") {
              reject(
                new Error(
                  "You are not authorized to perform this action. Please contact Support Team if you think this is a mistake."
                )
              );
            } else {
              reject(new Error(identityError.message));
            }
          }
        });
      });
    },

    updateMultipleServerPrograms(depotNames, region, updateAttribute) {
      return new Promise((resolve, reject) => {
        var AWSIoTData = require("aws-iot-device-sdk");
        var counter = 0,
          totalCount = depotNames.length;
        this.AWS.config.credentials.get(identityError => {
          if (!identityError) {
            try {
              const mqttClient = AWSIoTData.device({
                region: region,
                host: "a3ijg5gjke8m6j-ats.iot." + region + ".amazonaws.com",
                clientId: "elmsWebsiteClient_" + this.UserName,
                protocol: "wss",
                debug: false,
                reconnectPeriod: 0,
                accessKeyId: this.AWS.config.credentials.accessKeyId,
                secretKey: this.AWS.config.credentials.secretAccessKey,
                sessionToken: this.AWS.config.credentials.sessionToken
              });
              mqttClient.on("close", () => {
                // end the client connection again in case connection was automatically closed.
                mqttClient.end();
                resolve(depotNames);
              });
              mqttClient.on("connect", () => {
                // on connection, firstly subscribe to update confirmation topic.
                mqttClient.subscribe("ioc/+/elmsServer/cmd/confirmServerUpdate", { qos: 1 });
                // next, publish message to update the requested attribute and set a timeout period to end the client connection.
                depotNames.forEach(depotName => {
                  let messagePayload = {
                    updateAttribute: updateAttribute,
                    timestamp: new Date()
                  };
                  mqttClient.publish(
                    "ioc/" + depotName + "/elmsServer/cmd/issueServerUpdate",
                    JSON.stringify(messagePayload),
                    { qos: 1 }
                  );
                });
                setTimeout(() => {
                  // once timeout is reached, end the client connection.
                  mqttClient.end();
                }, this.getConnectionTimeout(depotNames.length));
              });
              mqttClient.on("error", () => {
                throw new Error("Error thrown by MQTT Client.");
              });
              mqttClient.on("message", (topic, payload) => {
                var depotName = topic.split("/")[1];
                let isUpdated = JSON.parse(payload).isUpdated;
                if (isUpdated) {
                  depotNames = depotNames.filter(depot => depot !== depotName);
                }
                counter += 1;
                // this means we have received a response from all the depots and can close the connection.
                if (counter === totalCount) {
                  mqttClient.end();
                }
              });
            } catch (error) {
              reject(error);
            }
          } else {
            if (identityError.name === "NotAuthorizedException") {
              reject(
                new Error(
                  "You are not authorized to perform this action. Please contact Support Team if you think this is a mistake."
                )
              );
            } else {
              reject(new Error(identityError.message));
            }
          }
        });
      });
    },

    async loadDepotData(selectedCompany, selectedDepot) {
      this.depotDataLoading = true;
      this.depotData = [];
      this.selectedRowKeys = [];
      this.selectedRows = [];
      var promiseArray = [];
      let company = this.SingleCompanyUser ? this.displayCompanies[0] : selectedCompany;
      // if the user has selected a company, iterate through the company's list of depots (if no depot selected) to create the query parameters.
      if (company) {
        if (selectedDepot) {
          let params = {
            entityId: "DPO#" + selectedDepot,
            objectType: "DPO",
            operation: "get",
            region: this.CompanyMap.get(company).region
          };
          promiseArray.push(this.sendRequest(params, {}, "get"));
        } else {
          let listOfLocations = this.CompanyMap.get(company).depots;
          // only proceed if the company has a non-empty list of depots.
          if (listOfLocations) {
            listOfLocations.forEach(depot => {
              let params = {
                entityId: "DPO#" + depot,
                objectType: "DPO",
                operation: "get",
                region: this.CompanyMap.get(company).region
              };
              promiseArray.push(this.sendRequest(params, {}, "get"));
            });
          }
        }
      } else {
        // else iterate through every company's list of depots to create the query parameters.
        this.CompanyMap.forEach(companyObject => {
          let listOfLocations = companyObject.depots;
          // only proceed if the company has a non-empty list of depots.
          if (listOfLocations) {
            listOfLocations.forEach(depot => {
              let params = {
                entityId: "DPO#" + depot,
                objectType: "DPO",
                operation: "get",
                region: companyObject.region
              };
              promiseArray.push(this.sendRequest(params, {}, "get"));
            });
          }
        });
      }
      try {
        let dataArray = await Promise.all(promiseArray);
        let data = dataArray.flat(Infinity);
        // check if no data is returned; display a message to user in that case.
        if (!data.length) {
          throw new Error("No data available for selected company!");
        }
        await this.getMultipleSyncStatus(data);
        this.depotData = data;
        this.depotEntriesFound = data.length;
      } catch (error) {
        this.$message.error("Error loading depot data: " + (error.message ? error.message : error));
        this.depotDataLoading = false;
        return;
      }
      this.depotDataLoading = false;
    },

    getMultipleSyncStatus(depotData) {
      return new Promise(resolve => {
        var AWSIoTData = require("aws-iot-device-sdk");
        this.AWS.config.credentials.get(identityError => {
          if (!identityError) {
            try {
              var depotData_usa = depotData.filter(
                depotObject => this.CompanyMap.get(depotObject.company).region == "us-west-2"
              );
              var depotData_sa = depotData.filter(
                depotObject => this.CompanyMap.get(depotObject.company).region == "sa-east-1"
              );
              var counter_usa = 0,
                counter_sa = 0;
              var mqttClient_usa, mqttClient_sa;
              if (depotData_sa.length) {
                mqttClient_sa = AWSIoTData.device({
                  region: "sa-east-1",
                  host: "a3ijg5gjke8m6j-ats.iot.sa-east-1.amazonaws.com",
                  clientId: "elmsWebsiteClient_sa-east-1_" + this.UserName,
                  protocol: "wss",
                  debug: false,
                  reconnectPeriod: 0,
                  accessKeyId: this.AWS.config.credentials.accessKeyId,
                  secretKey: this.AWS.config.credentials.secretAccessKey,
                  sessionToken: this.AWS.config.credentials.sessionToken
                });
                mqttClient_sa.on("close", () => {
                  // end the client connection again in case connection was automatically closed.
                  mqttClient_sa.end();
                });
                mqttClient_sa.on("connect", () => {
                  // on connection, firstly subscribe to update confirmation topics for all depots.
                  mqttClient_sa.subscribe("ioc/+/elmsServer/cmd/confirmServerUpdate", {
                    qos: 1
                  });
                  depotData_sa.forEach(depotObject => {
                    depotObject.syncStatus = "error";
                    // next, publish message to request the depotObjectRevision for each depot.
                    let messagePayload = {
                      updateAttribute: "depotObjectRevision",
                      timestamp: new Date()
                    };
                    mqttClient_sa.publish(
                      "ioc/" + depotObject.depot + "/elmsServer/cmd/issueServerUpdate",
                      JSON.stringify(messagePayload),
                      { qos: 1 }
                    );
                  });
                });
                mqttClient_sa.on("error", () => {
                  throw new Error("Error thrown by MQTT Client.");
                });
                mqttClient_sa.on("message", (topic, payload) => {
                  var depotName = topic.split("/")[1];
                  let reportedRevision = JSON.parse(payload).revision;
                  let depotObject = depotData_sa.find(depotObject => depotObject.depot === depotName);
                  if (depotObject) {
                    if (reportedRevision === depotObject.revision) {
                      depotObject.syncStatus = "true";
                    } else {
                      depotObject.syncStatus = "false";
                    }
                    counter_sa += 1;
                    // this means we have received a response from all the depots and can close the connection.
                    if (counter_sa === depotData_sa.length) {
                      mqttClient_sa.end();
                      // only resolve and return from the method if no depot data for USA is present.
                      if (!depotData_sa.length) {
                        resolve();
                      }
                    }
                  }
                });
              }
              if (depotData_usa.length) {
                mqttClient_usa = AWSIoTData.device({
                  region: "us-west-2",
                  host: "a3ijg5gjke8m6j-ats.iot.us-west-2.amazonaws.com",
                  clientId: "elmsWebsiteClient_us-west-2_" + this.UserName,
                  protocol: "wss",
                  debug: false,
                  reconnectPeriod: 0,
                  accessKeyId: this.AWS.config.credentials.accessKeyId,
                  secretKey: this.AWS.config.credentials.secretAccessKey,
                  sessionToken: this.AWS.config.credentials.sessionToken
                });
                mqttClient_usa.on("close", () => {
                  // end the client connection again in case connection was automatically closed.
                  mqttClient_usa.end();
                });
                mqttClient_usa.on("connect", () => {
                  // on connection, firstly subscribe to update confirmation topics for all depots.
                  mqttClient_usa.subscribe("ioc/+/elmsServer/cmd/confirmServerUpdate", {
                    qos: 1
                  });
                  depotData_usa.forEach(depotObject => {
                    depotObject.syncStatus = "error";
                    // next, publish message to request the depotObjectRevision for each depot.
                    let messagePayload = {
                      updateAttribute: "depotObjectRevision",
                      timestamp: new Date()
                    };
                    mqttClient_usa.publish(
                      "ioc/" + depotObject.depot + "/elmsServer/cmd/issueServerUpdate",
                      JSON.stringify(messagePayload),
                      { qos: 1 }
                    );
                  });
                });
                mqttClient_usa.on("error", () => {
                  throw new Error("Error thrown by MQTT Client.");
                });
                mqttClient_usa.on("message", (topic, payload) => {
                  var depotName = topic.split("/")[1];
                  let reportedRevision = JSON.parse(payload).revision;
                  let depotObject = depotData_usa.find(depotObject => depotObject.depot === depotName);
                  if (depotObject) {
                    if (reportedRevision === depotObject.revision) {
                      depotObject.syncStatus = "true";
                    } else {
                      depotObject.syncStatus = "false";
                    }
                    counter_usa += 1;
                    // this means we have received a response from all the depots and can close the connection.
                    if (counter_usa === depotData_usa.length) {
                      mqttClient_usa.end();
                      // only resolve and return from the method if no depot data for SA is present.
                      if (!depotData_sa.length) {
                        resolve();
                      }
                    }
                  }
                });
              }
              setTimeout(() => {
                // once timeout is reached, end the client connections.
                if (mqttClient_usa) {
                  mqttClient_usa.end();
                }
                if (mqttClient_sa) {
                  mqttClient_sa.end();
                }
                resolve();
              }, this.getConnectionTimeout(depotData_sa.length + depotData_usa.length));
            } catch (error) {
              resolve();
            }
          } else {
            resolve();
          }
        });
      });
    },

    getSingleSyncStatus(depotObject) {
      var AWSIoTData = require("aws-iot-device-sdk");
      var region = this.CompanyMap.get(depotObject.company).region;
      this.AWS.config.credentials.get(identityError => {
        if (!identityError) {
          try {
            this.depotDataLoading = true;
            depotObject.syncStatus = "error";
            const mqttClient = AWSIoTData.device({
              region: region,
              host: "a3ijg5gjke8m6j-ats.iot." + region + ".amazonaws.com",
              clientId: "elmsWebsiteClient_" + this.UserName,
              protocol: "wss",
              debug: false,
              reconnectPeriod: 0,
              accessKeyId: this.AWS.config.credentials.accessKeyId,
              secretKey: this.AWS.config.credentials.secretAccessKey,
              sessionToken: this.AWS.config.credentials.sessionToken
            });
            mqttClient.on("close", () => {
              // end the client connection again in case connection was automatically closed.
              mqttClient.end();
              this.depotDataLoading = false;
            });
            mqttClient.on("connect", () => {
              // on connection, firstly subscribe to update confirmation topic.
              mqttClient.subscribe("ioc/" + depotObject.depot + "/elmsServer/cmd/confirmServerUpdate", { qos: 1 });
              // next, publish message to update the requested attribute and set a timeout period to end the client connection.
              let messagePayload = {
                updateAttribute: "depotObjectRevision",
                timestamp: new Date()
              };
              mqttClient.publish(
                "ioc/" + depotObject.depot + "/elmsServer/cmd/issueServerUpdate",
                JSON.stringify(messagePayload),
                { qos: 1 }
              );
              setTimeout(() => {
                // once timeout is reached, end the client connection.
                mqttClient.end();
              }, 2500);
            });
            mqttClient.on("error", () => {
              throw new Error("Error thrown by MQTT Client.");
            });
            mqttClient.on("message", (topic, payload) => {
              let reportedRevision = JSON.parse(payload).revision;
              if (reportedRevision === depotObject.revision) {
                depotObject.syncStatus = "true";
              } else {
                depotObject.syncStatus = "false";
              }
              mqttClient.end();
            });
          } catch (error) {
            this.depotDataLoading = false;
          }
        } else {
          if (identityError.name === "NotAuthorizedException") {
            this.$message.error(
              "You are not authorized to perform this action. Please contact Support Team if you think this is a mistake."
            );
          } else {
            this.$message.error("Error retrieving depot's sync status - " + identityError.message);
          }
        }
      });
    },

    addChargers(formEvent) {
      formEvent.preventDefault();
      this.addChargerForm.validateFields(async (error, fieldValues) => {
        if (error) {
          return;
        }
        try {
          this.addChargerFormButtonLoading = true;
          let newDepotRecord = cloneDeep(this.addChargerFormInitialValues);
          let currentChargerList = newDepotRecord.chargerList;
          let addedChargerList = JSON.parse(fieldValues.chargerList.replace(/\s+/g, ""));
          addedChargerList.forEach(chargerRecord => {
            chargerRecord.depot = newDepotRecord.depot;
            chargerRecord.entityId = "CHG#" + chargerRecord.macID;
            chargerRecord.revision = new Date();
          });
          currentChargerList.push(...addedChargerList);
          newDepotRecord.chargerList = currentChargerList;
          var params = {
            objectType: "DPO",
            operation: "update",
            region: this.masterCompanyMap.get(newDepotRecord.company).region
          };
          var body = {
            depotRecord: this.prepareForDatabase(newDepotRecord),
            insertChargerObjects: true
          };
          await this.sendRequest(params, body, "post");
          let isServerUpdated = await this.updateSingleServerProgram(
            newDepotRecord.depot,
            this.masterCompanyMap.get(newDepotRecord.company).region,
            "chargerList"
          );
          if (!(isServerUpdated === true)) {
            throw new Error("Unable to update server program. Please try again or re-sync the server!");
          }
          this.addChargerFormButtonLoading = false;
          this.addChargerForm.resetFields();
          this.addChargerModalVisible = false;
          this.$message.success("Successfully added chargers and updated server program!");
          this.loadDepotData(
            this.depotSearchForm.getFieldValue("company"),
            this.depotSearchForm.getFieldValue("depot")
          );
        } catch (error) {
          this.addChargerFormButtonLoading = false;
          this.addChargerForm.resetFields();
          this.addChargerModalVisible = false;
          this.$message.error("Error adding chargers - " + (error.message ? error.message : error));
          this.loadDepotData(
            this.depotSearchForm.getFieldValue("company"),
            this.depotSearchForm.getFieldValue("depot")
          );
          return;
        }
      });
    },

    saveConfiguration(formEvent) {
      formEvent.preventDefault();
      this.editConfigurationForm.validateFields(async (error, fieldValues) => {
        if (error) {
          return;
        }
        try {
          this.editConfigurationFormButtonLoading = true;
          // update the values from the form, remove the keys, and make an API request.
          let newDepotRecord = cloneDeep(this.editConfigurationFormInitialValues);
          newDepotRecord.fleetList = JSON.stringify(fieldValues.fleetList);
          newDepotRecord.overriddenChargerCount = fieldValues.overriddenChargerCount == "true";
          newDepotRecord.maxChargingCurrent = fieldValues.maxChargingCurrent;
          newDepotRecord.maxConcurrentChargers = fieldValues.maxConcurrentChargers;
          newDepotRecord.maxSocToCharge = fieldValues.maxSocToCharge;
          newDepotRecord.timeoutForDisconnectAlert = fieldValues.timeoutForDisconnectAlert;
          var params = {
            objectType: "DPO",
            operation: "update",
            region: this.masterCompanyMap.get(newDepotRecord.company).region
          };
          var body = {
            depotRecord: this.prepareForDatabase(newDepotRecord)
          };
          await this.sendRequest(params, body, "post");
          let isServerUpdated = await this.updateSingleServerProgram(
            newDepotRecord.depot,
            this.masterCompanyMap.get(newDepotRecord.company).region,
            "configurationProperties"
          );
          if (!(isServerUpdated === true)) {
            throw new Error("Unable to update server program. Please try again or re-sync the server!");
          }
          this.editConfigurationFormButtonLoading = false;
          this.editConfigurationForm.resetFields();
          this.editConfigurationModalVisible = false;
          this.$message.success("Successfully saved configuration settings and updated server program!");
          this.loadDepotData(
            this.depotSearchForm.getFieldValue("company"),
            this.depotSearchForm.getFieldValue("depot")
          );
        } catch (error) {
          this.editConfigurationFormButtonLoading = false;
          this.editConfigurationForm.resetFields();
          this.editConfigurationModalVisible = false;
          this.$message.error("Error saving configuration settings - " + (error.message ? error.message : error));
          this.loadDepotData(
            this.depotSearchForm.getFieldValue("company"),
            this.depotSearchForm.getFieldValue("depot")
          );
          return;
        }
      });
    },

    async saveChargingSchedule(formEvent) {
      formEvent.preventDefault();
      if (!this.chargingScheduleDataSource.length) {
        this.$message.error("Enter at least one time slot!");
        return;
      }
      try {
        // update the values from the form, remove the keys, and make an API request.
        this.chargingScheduleButtonLoading = true;
        let newDepotRecord = cloneDeep(this.chargingScheduleIntitialValues);
        let newTimeSlots = cloneDeep(this.chargingScheduleDataSource);
        newTimeSlots.forEach(timeSlot => delete timeSlot.rowKey);
        newDepotRecord.timeSlots = JSON.stringify(newTimeSlots);
        var params = {
          objectType: "DPO",
          operation: "update",
          region: this.masterCompanyMap.get(newDepotRecord.company).region
        };
        var body = {
          depotRecord: this.prepareForDatabase(newDepotRecord)
        };
        await this.sendRequest(params, body, "post");
        let isServerUpdated = await this.updateSingleServerProgram(
          newDepotRecord.depot,
          this.masterCompanyMap.get(newDepotRecord.company).region,
          "timeSlots"
        );
        if (!(isServerUpdated === true)) {
          throw new Error("Unable to update server program. Please try again or re-sync the server!");
        }
        this.chargingScheduleForm.resetFields();
        this.chargingScheduleButtonLoading = false;
        this.chargingScheduleModalVisible = false;
        this.$message.success("Successfully saved charging restriction schedule and updated server program!");
        this.loadDepotData(this.depotSearchForm.getFieldValue("company"), this.depotSearchForm.getFieldValue("depot"));
      } catch (error) {
        this.chargingScheduleForm.resetFields();
        this.chargingScheduleButtonLoading = false;
        this.chargingScheduleModalVisible = false;
        this.$message.error("Error saving charging restriction schedule - " + (error.message ? error.message : error));
        this.loadDepotData(this.depotSearchForm.getFieldValue("company"), this.depotSearchForm.getFieldValue("depot"));
        return;
      }
    },

    async deleteCharger(currentDepotRecord, chargerRecord) {
      try {
        this.depotDataLoading = true;
        let newDepotRecord = cloneDeep(currentDepotRecord);
        let newChargerList = newDepotRecord.chargerList.filter(charger => {
          return charger.key !== chargerRecord.key;
        });
        newDepotRecord.chargerList = newChargerList;
        var params = {
          objectType: "DPO",
          operation: "update",
          region: this.masterCompanyMap.get(newDepotRecord.company).region
        };
        var body = {
          depotRecord: this.prepareForDatabase(newDepotRecord),
          insertChargerObjects: true
        };
        await this.sendRequest(params, body, "post");
        let isServerUpdated = await this.updateSingleServerProgram(
          newDepotRecord.depot,
          this.masterCompanyMap.get(newDepotRecord.company).region,
          "chargerList"
        );
        if (!(isServerUpdated === true)) {
          throw new Error("Unable to update server program. Please try again or re-sync the server!");
        }
        this.$message.success("Successfully deleted charger and updated server program!");
        this.loadDepotData(this.depotSearchForm.getFieldValue("company"), this.depotSearchForm.getFieldValue("depot"));
      } catch (error) {
        this.$message.error("Error deleting charger - " + (error.message ? error.message : error));
        this.loadDepotData(this.depotSearchForm.getFieldValue("company"), this.depotSearchForm.getFieldValue("depot"));
        return;
      }
    },

    deleteTimeSlot(timeSlotKey) {
      const currentDataSource = [...this.chargingScheduleDataSource];
      this.chargingScheduleDataSource = currentDataSource.filter(timeSlot => timeSlot.rowKey !== timeSlotKey);
      this.allowUnrestrictedCharging = this.checkZeroTimeSlot(this.chargingScheduleDataSource);
    },

    addTimeSlot(formEvent) {
      formEvent.preventDefault();
      this.addTimeSlotForm.validateFields((error, fieldValues) => {
        if (error) {
          return;
        }
        try {
          // depending on from where this method was called, add the time slot to the appropriate place.
          if (this.chargingScheduleModalVisible) {
            let currentDataSource = [...this.chargingScheduleDataSource];
            currentDataSource.push({
              noChargingStartTime: fieldValues.noChargingStartTime.format("HH:mm").toString(),
              noChargingStopTime: fieldValues.noChargingStopTime.format("HH:mm").toString(),
              isWeekendExcluded: fieldValues.isWeekendExcluded === "true",
              isOvernight: fieldValues.isOvernight === "true",
              rowKey: currentDataSource.length + 1
            });
            this.chargingScheduleDataSource = currentDataSource;
            this.allowUnrestrictedCharging = this.checkZeroTimeSlot(this.chargingScheduleDataSource);
            this.addTimeSlotModalVisible = false;
          } else {
            this.handleGroupAddTimeSlot(fieldValues);
          }
        } catch (error) {
          this.$message.error("Error adding time slot - " + (error.message ? error.message : error));
          this.addTimeSlotModalVisible = false;
          return;
        }
      });
    },

    editTimeSlot(formEvent) {
      formEvent.preventDefault();
      this.editTimeSlotForm.validateFields((error, fieldValues) => {
        if (error) {
          return;
        }
        try {
          let currentDataSource = [...this.chargingScheduleDataSource];
          currentDataSource = currentDataSource.filter(
            timeSlot => timeSlot.rowKey !== this.editTimeSlotFormInitialValues.rowKey
          );
          currentDataSource.push({
            noChargingStartTime: fieldValues.noChargingStartTime.format("HH:mm").toString(),
            noChargingStopTime: fieldValues.noChargingStopTime.format("HH:mm").toString(),
            isWeekendExcluded: fieldValues.isWeekendExcluded === "true",
            isOvernight: fieldValues.isOvernight === "true"
          });
          this.chargingScheduleDataSource = currentDataSource;
          this.allowUnrestrictedCharging = this.checkZeroTimeSlot(this.chargingScheduleDataSource);
          this.editTimeSlotModalVisible = false;
        } catch (error) {
          this.$message.error("Error saving time slot - " + (error.message ? error.message : error));
          this.editTimeSlotModalVisible = false;
          return;
        }
      });
    },

    addIndividualCharger(formEvent) {
      formEvent.preventDefault();
      this.addIndividualChargerForm.validateFields(async (error, fieldValues) => {
        if (error) {
          return;
        }
        try {
          let currentChargerList = this.addChargerForm.getFieldValue("chargerList");
          // if a chargerList already exists, add to the list.
          if (currentChargerList) {
            let jsonArray = JSON.parse(currentChargerList);
            jsonArray.push({
              chargerNumber: fieldValues.chargerNumber,
              macID: fieldValues.macID.trim(),
              priority: fieldValues.priority
            });
            this.addChargerForm.setFieldsValue({ chargerList: JSON.stringify(jsonArray) });
          } else {
            let jsonArray = [];
            jsonArray.push({
              chargerNumber: fieldValues.chargerNumber,
              macID: fieldValues.macID.trim(),
              priority: fieldValues.priority
            });
            this.addChargerForm.setFieldsValue({ chargerList: JSON.stringify(jsonArray) });
          }
          this.addIndividualChargerModalVisible = false;
        } catch (error) {
          this.$message.error("Error adding single charger - " + (error.message ? error.message : error));
          this.addIndividualChargerModalVisible = false;
          return;
        }
      });
    },

    async saveMoveChargers(formEvent) {
      formEvent.preventDefault();
      let error = this.moveChargersForm.getFieldError("transferTable");
      if (error) {
        return;
      }
      try {
        this.moveChargersLoadingPercentage = 20;
        this.moveChargersFormButtonLoading = true;
        let finalTargetKeys = this.moveChargersForm.getFieldValue("transferTable");
        let finalDataSource = cloneDeep(this.moveChargersData);
        let leftDepotRecord = cloneDeep(this.selectedRows[0]);
        leftDepotRecord.chargerList = finalDataSource.filter(
          chargerRecord => !finalTargetKeys.includes(chargerRecord.key)
        );
        leftDepotRecord.chargerList.forEach(chargerRecord => {
          // delete title if present and update the depot value.
          if (chargerRecord.title) {
            delete chargerRecord.title;
          }
          chargerRecord.depot = leftDepotRecord.depot;
        });
        let rightDepotRecord = cloneDeep(this.selectedRows[1]);
        rightDepotRecord.chargerList = finalDataSource.filter(chargerRecord =>
          finalTargetKeys.includes(chargerRecord.key)
        );
        rightDepotRecord.chargerList.forEach(chargerRecord => {
          // delete title if present and update the depot value.
          if (chargerRecord.title) {
            delete chargerRecord.title;
          }
          chargerRecord.depot = rightDepotRecord.depot;
        });
        let promiseArray = [];
        var leftDepotParams = {
          objectType: "DPO",
          operation: "update",
          region: this.masterCompanyMap.get(leftDepotRecord.company).region
        };
        var leftDepotBody = {
          depotRecord: this.prepareForDatabase(leftDepotRecord),
          insertChargerObjects: true
        };
        promiseArray.push(this.sendRequest(leftDepotParams, leftDepotBody, "post"));
        var rightDepotParams = {
          objectType: "DPO",
          operation: "update",
          region: this.masterCompanyMap.get(rightDepotRecord.company).region
        };
        var rightDepotBody = {
          depotRecord: this.prepareForDatabase(rightDepotRecord),
          insertChargerObjects: true
        };
        promiseArray.push(this.sendRequest(rightDepotParams, rightDepotBody, "post"));
        this.moveChargersLoadingPercentage = 40;
        await Promise.all(promiseArray);
        this.moveChargersLoadingPercentage = 60;
        let isLeftServerUpdated = await this.updateSingleServerProgram(
          leftDepotRecord.depot,
          this.masterCompanyMap.get(leftDepotRecord.company).region,
          "chargerList"
        );
        this.moveChargersLoadingPercentage = 80;
        let isRightServerUpdated = await this.updateSingleServerProgram(
          rightDepotRecord.depot,
          this.masterCompanyMap.get(rightDepotRecord.company).region,
          "chargerList"
        );
        if (!(isRightServerUpdated === true) || !(isLeftServerUpdated === true)) {
          throw new Error("Unable to update one of the server programs. Please try again or re-sync the servers!");
        }
        this.moveChargersLoadingPercentage = 100;
        this.moveChargersFormButtonLoading = false;
        this.moveChargersModalVisible = false;
        this.$message.success("Successfully updated charger lists and server programs!");
        this.loadDepotData(this.depotSearchForm.getFieldValue("company"), this.depotSearchForm.getFieldValue("depot"));
      } catch (error) {
        this.moveChargersFormButtonLoading = false;
        this.moveChargersModalVisible = false;
        this.$message.error("Error updating charger lists - " + (error.message ? error.message : error));
        this.loadDepotData(this.depotSearchForm.getFieldValue("company"), this.depotSearchForm.getFieldValue("depot"));
        return;
      }
    },

    async resyncServer(depotRecord) {
      try {
        let isServerResynced = await this.updateSingleServerProgram(
          depotRecord.depot,
          this.masterCompanyMap.get(depotRecord.company).region,
          "allConfigurationProperties"
        );
        if (!(isServerResynced === true)) {
          this.$message.error("Unable to resync server at this time. Please try again later or contact Support Team.");
        } else {
          this.$message.success("Successfully resynced server!");
        }
        this.loadDepotData(this.depotSearchForm.getFieldValue("company"), this.depotSearchForm.getFieldValue("depot"));
      } catch (error) {
        this.$message.error("Error while resyncing server - " + (error.message ? error.message : error));
        this.loadDepotData(this.depotSearchForm.getFieldValue("company"), this.depotSearchForm.getFieldValue("depot"));
      }
    },

    showResyncConfirm(depotRecord) {
      let content =
        "NOTE - Resyncing the server will not re-initialize the AWS client; server must be restarted for that.";
      this.$confirm({
        title: "Confirm server resync for " + depotRecord.depot + "?",
        content: content,
        okText: "Yes",
        cancelText: "No",
        onOk: () => {
          return this.resyncServer(depotRecord);
        }
      });
    },

    showAllowUnrestrictedChargingConfirm() {
      let content =
        "NOTE - Selecting this will disregard all present charging restrictions and allow charging 24/7 for all the selected depots.";
      this.$confirm({
        title: "Allow Unrestricted Charging for Selected Depots?",
        content: content,
        okText: "Yes",
        cancelText: "No",
        onOk: () => {
          return this.handleGroupUnrestrictedCharging(true);
        }
      });
    },

    showRemoveUnrestrictedChargingConfirm() {
      let content =
        "Selecting this will remove unrestricted charging for the selected depots and kick in the present charging restrictions. NOTE - This change will not go through for depots who do not have any other charging restrictions present.";
      this.$confirm({
        title: "Remove Unrestricted Charging for Selected Depots?",
        content: content,
        okText: "Yes",
        cancelText: "No",
        onOk: () => {
          return this.handleGroupUnrestrictedCharging(false);
        }
      });
    },

    async handleGroupUnrestrictedCharging(isChargingOn) {
      if (isChargingOn) {
        let selectedData = cloneDeep(this.selectedRows);
        try {
          let promiseArray = [];
          let depotNames = [];
          let region = this.CompanyMap.get(this.selectedRows[0].company).region;
          selectedData.forEach(depot => {
            let currentTimeSlots = JSON.parse(depot.timeSlots);
            if (!this.checkZeroTimeSlot(currentTimeSlots)) {
              currentTimeSlots.push({
                noChargingStartTime: "00:00",
                noChargingStopTime: "00:00",
                isWeekendExcluded: false,
                isOvernight: false
              });
              depot.timeSlots = JSON.stringify(currentTimeSlots);
              let params = {
                objectType: "DPO",
                operation: "update",
                region: region
              };
              var body = {
                depotRecord: this.prepareForDatabase(depot)
              };
              promiseArray.push(this.sendRequest(params, body, "post"));
              depotNames.push(depot.depot);
            }
          });
          // only proceed to send API requests and update server program if required for any selected depot.
          if (promiseArray.length) {
            await Promise.all(promiseArray);
            let returnedDepots = await this.updateMultipleServerPrograms(depotNames, region, "timeSlots");
            if (!returnedDepots.length) {
              this.$message.success(
                "Successfully allowed unrestricted charging to selected depots and updated server programs!"
              );
            } else {
              this.$error({
                title: "Error Updating Server Program(s)",
                content:
                  "Unable to update server program for the following depot(s): " +
                  returnedDepots.join(", ") +
                  ". Please try again or re-sync the server!"
              });
            }
            this.loadDepotData(
              this.depotSearchForm.getFieldValue("company"),
              this.depotSearchForm.getFieldValue("depot")
            );
          } else {
            this.$message.warning("Selected depots already have unrestricted charging.");
          }
        } catch (error) {
          this.$message.error(
            "Error while adding unrestricted charging to selected depots - " + (error.message ? error.message : error)
          );
          this.loadDepotData(
            this.depotSearchForm.getFieldValue("company"),
            this.depotSearchForm.getFieldValue("depot")
          );
        }
      } else {
        let selectedData = cloneDeep(this.selectedRows);
        try {
          let promiseArray = [];
          let depotNames = [];
          let region = this.CompanyMap.get(this.selectedRows[0].company).region;
          selectedData.forEach(depot => {
            let currentTimeSlots = JSON.parse(depot.timeSlots);
            if (this.checkZeroTimeSlot(currentTimeSlots) && currentTimeSlots.length > 1) {
              currentTimeSlots = currentTimeSlots.filter(timeSlot => {
                return timeSlot.noChargingStartTime != "00:00" && timeSlot.noChargingStopTime != "00:00";
              });
              depot.timeSlots = JSON.stringify(currentTimeSlots);
              let params = {
                objectType: "DPO",
                operation: "update",
                region: region
              };
              var body = {
                depotRecord: this.prepareForDatabase(depot)
              };
              promiseArray.push(this.sendRequest(params, body, "post"));
              depotNames.push(depot.depot);
            }
          });
          // only proceed to send API requests and update server program if required for any selected depot.
          if (promiseArray.length) {
            await Promise.all(promiseArray);
            let returnedDepots = await this.updateMultipleServerPrograms(depotNames, region, "timeSlots");
            if (!returnedDepots.length) {
              this.$message.success(
                "Successfully removing unrestricted charging from selected depots and updated server programs!"
              );
            } else {
              this.$error({
                title: "Error Updating Server Program(s)",
                content:
                  "Unable to update server program for the following depot(s): " +
                  returnedDepots.join(", ") +
                  ". Please try again or re-sync the server!"
              });
            }
            this.loadDepotData(
              this.depotSearchForm.getFieldValue("company"),
              this.depotSearchForm.getFieldValue("depot")
            );
          } else {
            this.$message.warning(
              "Selected depots do not have unrestricted charging or have only one charging restriction."
            );
          }
        } catch (error) {
          this.$message.error(
            "Error while removing unrestricted charging from selected depots - " +
              (error.message ? error.message : error)
          );
          this.loadDepotData(
            this.depotSearchForm.getFieldValue("company"),
            this.depotSearchForm.getFieldValue("depot")
          );
        }
      }
    },

    async handleGroupAddTimeSlot(fieldValues) {
      let selectedData = cloneDeep(this.selectedRows);
      try {
        this.addTimeSlotFormButtonLoading = true;
        let promiseArray = [];
        let depotNames = [];
        let region = this.CompanyMap.get(this.selectedRows[0].company).region;
        selectedData.forEach(depot => {
          let currentTimeSlots = JSON.parse(depot.timeSlots);
          let addedTimeSlot = {
            noChargingStartTime: fieldValues.noChargingStartTime.format("HH:mm").toString(),
            noChargingStopTime: fieldValues.noChargingStopTime.format("HH:mm").toString(),
            isWeekendExcluded: fieldValues.isWeekendExcluded === "true",
            isOvernight: fieldValues.isOvernight === "true"
          };
          if (!this.checkSpecificTimeSlot(addedTimeSlot, currentTimeSlots)) {
            currentTimeSlots.push(addedTimeSlot);
            depot.timeSlots = JSON.stringify(currentTimeSlots);
            let params = {
              objectType: "DPO",
              operation: "update",
              region: region
            };
            var body = {
              depotRecord: this.prepareForDatabase(depot)
            };
            promiseArray.push(this.sendRequest(params, body, "post"));
            depotNames.push(depot.depot);
          }
        });
        // only proceed to send API requests and update server program if required for any selected depot.
        if (promiseArray.length) {
          await Promise.all(promiseArray);
          let returnedDepots = await this.updateMultipleServerPrograms(depotNames, region, "timeSlots");
          if (!returnedDepots.length) {
            this.$message.success(
              "Successfully added no charging time slot to selected depots and updated server programs!"
            );
          } else {
            this.$error({
              title: "Error Updating Server Program(s)",
              content:
                "Unable to update server program for the following depot(s): " +
                returnedDepots.join(", ") +
                ". Please try again or re-sync the server!"
            });
          }
          this.loadDepotData(
            this.depotSearchForm.getFieldValue("company"),
            this.depotSearchForm.getFieldValue("depot")
          );
        } else {
          this.$message.warning("Selected depots already have the added time slot.");
        }
        this.addTimeSlotFormButtonLoading = false;
        this.addTimeSlotModalVisible = false;
      } catch (error) {
        this.$message.error(
          "Error while adding no charging time slot to selected depots - " + (error.message ? error.message : error)
        );
        this.addTimeSlotFormButtonLoading = false;
        this.addTimeSlotModalVisible = false;
        this.loadDepotData(this.depotSearchForm.getFieldValue("company"), this.depotSearchForm.getFieldValue("depot"));
      }
    },

    handleGroupActionClick(clickEvent) {
      switch (clickEvent.key) {
        case "moveChargers":
          this.handleMoveChargers();
          break;
        case "allowUnrestrictedCharging":
          this.showAllowUnrestrictedChargingConfirm();
          break;
        case "removeUnrestrictedCharging":
          this.showRemoveUnrestrictedChargingConfirm();
          break;
        case "addTimeSlot":
          this.addTimeSlotModalVisible = true;
          break;
        default:
          break;
      }
    },

    customAddFormValidator(rule, value, callback) {
      if (value) {
        try {
          let jsonList = JSON.parse(value);
          jsonList.forEach(chargerRecord => {
            if (!chargerRecord.macID || !chargerRecord.chargerNumber || !chargerRecord.priority) {
              throw new Error("One of the required attributes missing.");
            } else if (chargerRecord.macID.length != 12) {
              throw new Error("MAC ID for charger number " + chargerRecord.chargerNumber + " must be 12 characters.");
            } else {
              let currentChargerNumber, currentMacID;
              if (
                this.addChargerFormInitialValues.chargerList.some(currentCharger => {
                  currentChargerNumber = currentCharger.chargerNumber;
                  return currentCharger.macID === chargerRecord.macID;
                })
              ) {
                throw new Error(
                  "Duplicate MAC ID for new charger number " +
                    chargerRecord.chargerNumber +
                    " and current charger number " +
                    currentChargerNumber +
                    "."
                );
              } else if (
                this.addChargerFormInitialValues.chargerList.some(currentCharger => {
                  currentMacID = currentCharger.macID;
                  return currentCharger.chargerNumber === chargerRecord.chargerNumber;
                })
              ) {
                throw new Error(
                  "Duplicate charger numbers for new MAC ID " +
                    chargerRecord.macID +
                    " and current MAC ID " +
                    currentMacID +
                    "."
                );
              } else if (
                this.addChargerFormInitialValues.chargerList.some(currentCharger => {
                  currentChargerNumber = currentCharger.chargerNumber;
                  return currentCharger.priority === chargerRecord.priority;
                })
              ) {
                throw new Error(
                  "Duplicate priority for new charger number " +
                    chargerRecord.chargerNumber +
                    " and current charger number " +
                    currentChargerNumber +
                    "."
                );
              }
            }
          });
          callback();
        } catch (error) {
          switch (error.name) {
            case "SyntaxError":
              callback("Invalid JSON Format. Error: " + error.message);
              break;
            case "TypeError":
              callback("Invalid input. Error: Input must be a JSON array.");
              break;
            default:
              callback("Error: " + error.message);
              break;
          }
        }
      } else {
        callback("Charger List cannot be empty!");
      }
    },

    customFleetListValidator(rule, value, callback) {
      if (value) {
        if (value.length > 1) {
          callback("Only one fleet per depot is allowed during this transition phase.");
        }
        callback();
      } else {
        callback();
      }
    },

    customEditFormValidator(rule, value, callback) {
      switch (rule.field) {
        case "chargerNumber":
          if (value) {
            let currentMacID;
            if (
              this.editChargerFormInitialValues.chargerList.some(currentCharger => {
                currentMacID = currentCharger.macID;
                return (
                  currentCharger.chargerNumber === value &&
                  currentCharger.key != this.editChargerFormInitialValues.selectedChargerKey
                );
              })
            ) {
              callback("Duplicate charger number - Currently assigned to MAC ID " + currentMacID + ".");
            }
          } else {
            callback("Please enter a charger number!");
          }
          callback();
          break;
        case "priority":
          if (value) {
            let currentChargerNumber;
            if (
              this.editChargerFormInitialValues.chargerList.some(currentCharger => {
                currentChargerNumber = currentCharger.chargerNumber;
                return (
                  currentCharger.priority === value &&
                  currentCharger.key != this.editChargerFormInitialValues.selectedChargerKey
                );
              })
            ) {
              callback("Duplicate priority - Currently assigned to charger number " + currentChargerNumber + ".");
            }
          } else {
            callback("Please enter a priority!");
          }
          callback();
          break;
        case "macID":
          if (value) {
            if (value.length != 12) {
              callback("MAC ID must be exactly 12 characters.");
            } else {
              let currentChargerNumber;
              if (
                this.editChargerFormInitialValues.chargerList.some(currentCharger => {
                  currentChargerNumber = currentCharger.chargerNumber;
                  return (
                    currentCharger.macID === value &&
                    currentCharger.key != this.editChargerFormInitialValues.selectedChargerKey
                  );
                })
              ) {
                callback("Duplicate MAC ID - Currently under charger number " + currentChargerNumber + ".");
              }
            }
          } else {
            callback("Please enter a MAC ID!");
          }
          callback();
          break;
      }
    },

    customMoveChargersValidator(targetKeys) {
      try {
        // separate the target and source charger lists and check if any duplicate records are present.
        let targetChargerList = this.moveChargersData.filter(chargerRecord => targetKeys.includes(chargerRecord.key));
        let sourceChargerList = this.moveChargersData.filter(chargerRecord => !targetKeys.includes(chargerRecord.key));
        let chargerNumberList = [],
          priorityList = [];
        targetChargerList.forEach(chargerRecord => {
          if (chargerNumberList.includes(chargerRecord.chargerNumber)) {
            throw new Error(
              "Duplicate Charger Number found in " +
                this.moveChargersTitles[1] +
                ": " +
                chargerRecord.chargerNumber +
                "."
            );
          } else {
            chargerNumberList.push(chargerRecord.chargerNumber);
          }
          if (priorityList.includes(chargerRecord.priority)) {
            throw new Error(
              "Duplicate priority found in " + this.moveChargersTitles[1] + ": " + chargerRecord.priority + "."
            );
          } else {
            priorityList.push(chargerRecord.priority);
          }
        });
        chargerNumberList = [];
        priorityList = [];
        sourceChargerList.forEach(chargerRecord => {
          if (chargerNumberList.includes(chargerRecord.chargerNumber)) {
            throw new Error(
              "Duplicate Charger Number found in " +
                this.moveChargersTitles[0] +
                ": " +
                chargerRecord.chargerNumber +
                "."
            );
          } else {
            chargerNumberList.push(chargerRecord.chargerNumber);
          }
          if (priorityList.includes(chargerRecord.priority)) {
            throw new Error(
              "Duplicate priority found in " + this.moveChargersTitles[0] + ": " + chargerRecord.priority + "."
            );
          } else {
            priorityList.push(chargerRecord.priority);
          }
        });
        // if no errors are thrown, simply set the value of the transferTable component.
        this.moveChargersForm.setFields({
          transferTable: {
            value: targetKeys
          }
        });
      } catch (errorMessage) {
        // set the value of the transferTable component along with the error message.
        this.moveChargersForm.setFields({
          transferTable: {
            value: targetKeys,
            errors: [errorMessage]
          }
        });
      }
    },

    handleUploadChange(info) {
      let fileList = [...info.fileList];
      // check the status of the file upload.
      if (info.file.status === "done") {
        this.$message.success(`${info.file.name} file uploaded successfully.`);
        // once file is uploaded successfully, parse the content and convert to JSON.
        let reader = new FileReader();
        reader.onload = e => {
          try {
            const csv2json = require("csvjson-csv2json");
            const json = csv2json(e.target.result, { parseNumbers: true });
            this.addChargerForm.setFieldsValue({ chargerList: JSON.stringify(json) });
          } catch (error) {
            this.addChargerForm.setFieldsValue({ chargerList: "Invalid CSV Format" });
          }
        };
        reader.readAsText(info.file.originFileObj);
      } else if (info.file.status === "error") {
        this.$message.error(`${info.file.name} file upload failed.`);
      }

      // disable upload button after one upload.
      if (fileList.length != 0) {
        this.uploadCSVButtonDisabled = true;
      } else {
        this.uploadCSVButtonDisabled = false;
        this.addChargerForm.resetFields(["chargerList"]);
      }

      this.uploadFileList = fileList;
    },

    // function to remove all the locally added attributes from a depotRecord and update the revision & updatedBy attributes.
    prepareForDatabase(depotRecord) {
      delete depotRecord.key;
      delete depotRecord.numberOfChargers;
      delete depotRecord.syncStatus;
      depotRecord.chargerList.forEach(chargerRecord => {
        if (chargerRecord.key) {
          delete chargerRecord.key;
        }
        chargerRecord.revision = new Date(chargerRecord.revision).toISOString();
      });
      depotRecord.chargerList = JSON.stringify(depotRecord.chargerList);
      depotRecord.revision = new Date();
      depotRecord.updatedBy = this.UserName;

      // hotfix for allDayChargingAvailable bug on server program side.
      let currentTimeSlots = JSON.parse(depotRecord.timeSlots);
      let zeroTimeSlots = currentTimeSlots.filter(timeSlot => {
        return timeSlot.noChargingStartTime == "00:00" && timeSlot.noChargingStopTime == "00:00";
      });
      if (zeroTimeSlots.length) {
        currentTimeSlots = currentTimeSlots = currentTimeSlots.filter(timeSlot => {
          return timeSlot.noChargingStartTime != "00:00" && timeSlot.noChargingStopTime != "00:00";
        });
        currentTimeSlots.push({
          noChargingStartTime: "00:00",
          noChargingStopTime: "00:00",
          isWeekendExcluded: false,
          isOvernight: false
        });
        depotRecord.timeSlots = JSON.stringify(currentTimeSlots);
      }
      return JSON.stringify(depotRecord);
    },

    dummyUploadRequest({ onSuccess }) {
      setTimeout(() => {
        onSuccess("Upload Successful");
      }, 0);
    },

    setTotalNumberOfChargers(totalChargerCount) {
      this.totalNumberOfChargers = totalChargerCount;
    },

    onSelectChange(selectedRowKeys, selectedRows) {
      this.selectedRowKeys = selectedRowKeys;
      this.selectedRows = selectedRows;
    },

    handleMoveChargers() {
      let targetKeys = [];
      let leftDepot = cloneDeep(this.selectedRows[0]);
      leftDepot.chargerList.forEach(chargerRecord => {
        chargerRecord.key = chargerRecord.entityId;
        chargerRecord.title = chargerRecord.macID;
      });
      let rightDepot = cloneDeep(this.selectedRows[1]);
      rightDepot.chargerList.forEach(chargerRecord => {
        chargerRecord.key = chargerRecord.entityId;
        chargerRecord.title = chargerRecord.macID;
        targetKeys.push(chargerRecord.entityId);
      });
      this.moveChargersData = leftDepot.chargerList.concat(rightDepot.chargerList);
      this.moveChargersCacheData = cloneDeep(this.moveChargersData);
      this.moveChargersTitles = [leftDepot.depot, rightDepot.depot];
      this.moveChargersTargetKeys = targetKeys;
      this.moveChargersForm.getFieldDecorator("transferTable", {
        valuePropName: "targetKeys"
      });
      this.moveChargersForm.setFieldsValue({ transferTable: targetKeys });
      this.moveChargersModalVisible = true;
    },

    handleMoveChargersChange(nextTargetKeys) {
      this.moveChargersTargetKeys = nextTargetKeys;
      this.customMoveChargersValidator(nextTargetKeys);
    },

    getRowSelection({ selectedKeys, itemSelectAll, itemSelect }) {
      return {
        onSelectAll(selected, selectedRows) {
          const treeSelectedKeys = selectedRows.filter(item => !item.disabled).map(({ key }) => key);
          const diffKeys = selected
            ? difference(treeSelectedKeys, selectedKeys)
            : difference(selectedKeys, treeSelectedKeys);
          itemSelectAll(diffKeys, selected);
        },
        onSelect({ key }, selected) {
          itemSelect(key, selected);
        },
        selectedRowKeys: selectedKeys,
        fixed: true
      };
    },

    editChargerRecord(key) {
      const newData = [...this.moveChargersData];
      const target = newData.filter(item => key === item.key)[0];
      this.editingKey = key;
      if (target) {
        target.editable = true;
        this.moveChargersData = newData;
      }
    },

    handleChargerRecordChange(value, key, columnName) {
      if (!value || !Number.isInteger(value)) {
        this.$message.warning("Value must be a valid integer!");
      } else {
        const newData = [...this.moveChargersData];
        const target = newData.filter(item => key === item.key)[0];
        if (target) {
          target[columnName] = value;
          this.moveChargersData = newData;
        }
      }
    },

    saveChargerRecord(key) {
      const newData = [...this.moveChargersData];
      const newCacheData = [...this.moveChargersCacheData];
      const target = newData.filter(item => key === item.key)[0];
      const targetCache = newCacheData.filter(item => key === item.key)[0];
      if (target && targetCache) {
        delete target.editable;
        target.revision = new Date();
        this.moveChargersData = newData;
        Object.assign(targetCache, target);
        this.moveChargersCacheData = newCacheData;
      }
      this.editingKey = "";
      this.customMoveChargersValidator(this.moveChargersTargetKeys);
    },

    cancelChargerRecordChange(key) {
      const newData = [...this.moveChargersData];
      const target = newData.filter(item => key === item.key)[0];
      this.editingKey = "";
      if (target) {
        Object.assign(target, this.moveChargersCacheData.filter(item => key === item.key)[0]);
        delete target.editable;
        this.moveChargersData = newData;
      }
    },

    getConnectionTimeout(dataSize) {
      if (dataSize === 1) {
        return 2500;
      } else if (dataSize <= 5) {
        return 5000;
      } else if (dataSize <= 30) {
        return 10000;
      } else {
        return 12500;
      }
    },

    onCompanyChange(value) {
      this.depotSearchForm.resetFields(["depot"]);
      if (!value) {
        this.depotDisabled = true;
      } else {
        this.depotDisabled = false;
        this.depotList = this.CompanyMap.get(value).depots.sort();
      }
    },

    createParamsUrl(paramsObject) {
      var paramsUrl = "";
      var counter = 0;
      var totalCount = Object.keys(paramsObject).length;
      for (var key in paramsObject) {
        if (paramsObject.hasOwnProperty(key)) {
          paramsUrl += key + "=" + paramsObject[key];
          if (counter != totalCount - 1) {
            paramsUrl += "&";
          }
          counter += 1;
        }
      }
      return paramsUrl.replace("#", "%23");
    },

    checkZeroTimeSlot(data) {
      let zeroTimeSlotFound = false;
      if (data.length) {
        data.forEach(timeSlot => {
          if (timeSlot.noChargingStartTime == "00:00" && timeSlot.noChargingStopTime == "00:00") {
            zeroTimeSlotFound = true;
            return;
          }
        });
        return zeroTimeSlotFound;
      }
      return zeroTimeSlotFound;
    },

    checkSpecificTimeSlot(timeSlot, data) {
      return (
        data.filter(
          currentTimeSlot =>
            currentTimeSlot.noChargingStartTime == timeSlot.noChargingStartTime &&
            currentTimeSlot.noChargingStopTime == timeSlot.noChargingStopTime &&
            currentTimeSlot.isOvernight == timeSlot.isOvernight &&
            currentTimeSlot.isWeekendExcluded == timeSlot.isWeekendExcluded
        ).length > 0
      );
    },

    handleCheckboxChange(event) {
      this.allowUnrestrictedCharging = event.target.checked;
      if (this.allowUnrestrictedCharging && !this.checkZeroTimeSlot(this.chargingScheduleDataSource)) {
        let currentDataSource = [...this.chargingScheduleDataSource];
        currentDataSource.push({
          noChargingStartTime: "00:00",
          noChargingStopTime: "00:00",
          isWeekendExcluded: false,
          isOvernight: false,
          rowKey: currentDataSource.length + 1
        });
        this.chargingScheduleDataSource = currentDataSource;
      } else if (!this.allowUnrestrictedCharging && this.checkZeroTimeSlot(this.chargingScheduleDataSource)) {
        const currentDataSource = [...this.chargingScheduleDataSource];
        this.chargingScheduleDataSource = currentDataSource.filter(timeSlot => {
          return timeSlot.noChargingStartTime != "00:00" && timeSlot.noChargingStopTime != "00:00";
        });
      }
    }
  },

  created() {
    if (this.SingleCompanyUser) {
      this.onCompanyChange(this.displayCompanies[0]);
    }
    // check if user has admin access.
    this.isAdminUser = this.AccessGroups.includes("admin");
  }
};
</script>

<style>
.ant-form-item {
  margin-bottom: 5px;
}

.p {
  margin: 0;
}

.a-button {
  margin: 0 auto;
  display: block;
}

#addTimeSlot {
  z-index: 2000;
  position: relative;
}

#addUser {
  z-index: 2000;
  position: relative;
}

#backModal {
  z-index: 1000;
  position: relative;
}
</style>
